import { IconButton } from '@mui/material';
import { CloseCircle } from 'iconsax-react';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { createContext, useReducer, ReactElement, useContext, useEffect, useRef } from 'react';
import {
  SET_LOGGED_IN_USER_IDS,
  SET_PNL,
  SET_PREPARE_DATA_PROGRESS,
  SET_RECOMMENDATIONS,
  SET_SUBSCRIBED,
  TOGGLE_ORDER_REQUEST_STATUS,
  TOGGLE_USER_STATUS,
  TOGGLE_ORDER_STATUS
} from 'store/reducers/actions';
import websocketReducer from 'store/reducers/websocket';
import { LiveTicks, WebsocketContextType } from 'types/custom/websocket';
import playSound from 'utils/play-notification-sound';
import WebSocketService from 'websocket/config';
import { useFirebaseContext } from './FirebaseContext';
import { initialState } from 'store/reducers/websocket';
import { useDataContext } from './DataContext';

const WebSocketContext = createContext<WebsocketContextType | null>(null);

export const WebsocketContextProvider = ({ children }: { children: ReactElement }) => {
  const { isLoggedIn } = useFirebaseContext();
  const { fetchAllNotifications } = useDataContext();
  const [state, dispatch] = useReducer(websocketReducer, initialState);

  // ✅ Use useRef instead of useState for liveTicks (no re-renders)
  const liveTicksRef = useRef<LiveTicks>({});

  const handleMessageForTicks = (message: any) => {
    try {
      if (message) {
        // ✅ Mutate ref instead of causing re-render
        // liveTicksRef.current[parsedBody.stockId] = parsedBody;
      }
    } catch (error) {
      console.error('Error parsing message body:', error);
    }
  };

  const handleMessageForTickBatch = (message: any) => {
    try {
      if (!message) return;
      for (const tick of message) {
        if (tick?.stockId) {
          liveTicksRef.current[tick.stockId] = tick;
        }
      }
    } catch (error) {
      console.error('Error parsing tick batch:', error);
    }
  };

  const handleMessageForPnl = (message: any) => {
    try {
      if (message) {
        dispatch({
          type: SET_PNL,
          payload: message
        });
      }
    } catch (error) {
      console.error('Error parsing message body:', error);
    }
  };

  const handleNotification = (message: any) => {
    try {
      if (message) {
        if (message.type === 'login' || message.type === 'logout') {
          dispatch({ type: TOGGLE_USER_STATUS });
          fetchAllNotifications();
          enqueueSnackbar(message.message, {
            autoHideDuration: 9000,
            variant: 'success',
            anchorOrigin: { vertical: 'top', horizontal: 'right' },
            action: (key) => (
              <IconButton size="small" aria-label="close" color="inherit" onClick={() => closeSnackbar(key)}>
                <CloseCircle fontSize="small" />
              </IconButton>
            )
          });
          playSound('fresh');
        } else if (message.type === 'orderPlaced') {
          dispatch({type: TOGGLE_ORDER_STATUS})
          playSound('elegant');
        } else if (message.type === 'orderFailed') {
          dispatch({type: TOGGLE_ORDER_STATUS})
          playSound('error');
        } else if (message.type === 'orderModified') {
          dispatch({ type: TOGGLE_ORDER_STATUS });
          playSound('ping');
        } else if (message.type === 'orderRequest') {
          dispatch({ type: TOGGLE_ORDER_REQUEST_STATUS });
          playSound('ping');
        } else if (message.type === 'subscribed') {
          dispatch({ type: SET_SUBSCRIBED });
          playSound('fresh');
        } else if (message.type === 'recommendation') {
          dispatch({ type: SET_RECOMMENDATIONS });
          playSound('string');
        }
      }
    } catch (error) {
      console.error('Error parsing message body:', error);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      WebSocketService.connect()
        .then(() => {
          WebSocketService.subscribe('/topic/notification', handleNotification);
          WebSocketService.subscribe('/topic/livepnl', handleMessageForPnl);
          WebSocketService.subscribe('/topic/liveticks/batch', handleMessageForTickBatch);
        })
        .catch((error) => console.error('Failed to connect to WebSocket', error));
    }

    return () => {
      WebSocketService.unsubscribeAll();
    };
  }, [isLoggedIn]);

  const connectForLiveTicks = (stockIds: string[]) => {
    for (const stockId of stockIds) {
      WebSocketService.subscribe(`/topic/liveticks/${stockId}`, handleMessageForTicks);
    }
  };

  const setLoggedInUserIds = (userIds: string[]) => {
    dispatch({ type: SET_LOGGED_IN_USER_IDS, payload: userIds });
  };

  const setPrepareDataProgress = (progress: number) => {
    dispatch({ type: SET_PREPARE_DATA_PROGRESS, payload: progress });
  };

  return (
    <WebSocketContext.Provider
      value={{
        ...state,
        setLoggedInUserIds,
        liveTicks: liveTicksRef,
        connectForLiveTicks,
        setPrepareDataProgress
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebsocketContext = () => {
  const context = useContext(WebSocketContext);
  if (!context) throw new Error('useWebsocketContext must be used within a WebsocketContextProvider');
  return context;
};

export default WebSocketContext;
